import * as React from 'react'

import PanesHeader from 'modules/core/components/PanesHeader'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import { trackingEvents } from 'modules/core/lib/Tracking/events'
import i18n from 'modules/core/lib/i18n'

import { useReportTracking } from 'modules/reports/hooks/useReportTracking'
import templatesCollection from 'modules/reports/store/collections/templates'

import Categories from './Categories'
import List from './List'
import SearchBar from './SearchBar'
import styles from './index.module.css'
import { getCategories } from './muncher'
// Hooks
import useForm from './useForm'
import useTemplates from './useTemplates'

interface Props {
  selectedCategory: string | null | undefined
  loadingTemplates: boolean
}

const Content = ({ selectedCategory, loadingTemplates }: Props) => {
  const { track } = useReportTracking()

  const {
    has_time_tracking: timeTracking,
    has_shift_management: shiftManagement,
    has_esignature: esignature,
    has_time_off: timeOff,
    has_documents: documents,
    has_ats: ats,
  } = useFeatures([
    'time_tracking',
    'shift_management',
    'esignature',
    'time_off',
    'documents',
    'ats',
  ])

  const featureLookup: Record<string, boolean> = React.useMemo(
    () => ({
      time_tracking: timeTracking,
      shift_management: shiftManagement,
      esignature,
      time_off: timeOff,
      documents,
      ats,
    }),
    [timeTracking, shiftManagement, esignature, timeOff, documents, ats]
  )

  const featureChecker = React.useCallback(
    (feature: string) => featureLookup[feature] ?? false,
    [featureLookup]
  )

  const categories = React.useMemo<Record<string, string>>(() => {
    return getCategories(templatesCollection.models, featureChecker)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingTemplates, featureChecker])

  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const [selectedCategories, setSelectedCategories] = React.useState<string[]>([])

  const form = useForm({
    categories,
  })

  const templates = useTemplates({
    search: debouncedSearch,
    selectedCategories,
  })
  React.useEffect(() => {
    if (!selectedCategory) return

    const key = `reports.templates.categories.${selectedCategory}`

    if (!form.has(key)) return

    form.get(key).set(true)
    setSelectedCategories([key])
    track(trackingEvents.reports.timeoffReportVisited)
  }, [track, form, selectedCategory])

  return (
    <div className={styles.templatesWrapper}>
      <div className={styles.filters}>
        <PanesHeader
          title={i18n.t('custom_reports.reports_library.title')}
          description={i18n.t('custom_reports.reports_library.description')}
          size="small"
        />
        <Categories
          form={form}
          categories={categories}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
        />
      </div>
      <div className={styles.templates}>
        <SearchBar field={search} setField={onSearch} />
        <List templates={templates} />
      </div>
    </div>
  )
}

export default Content
